import { lazy } from 'react'

// project imports
import MainLayout from '../layout/MainLayout'
import MinimalLayout from '../layout/MinimalLayout'
import NetflixLayout from '../layout/NetflixLayout'
import SimpleHeaderLayout from '../layout/SimpleHeaderLayout'
import YoutubeKidsLayout from '../layout/YoutubeKidsLayout'

import Loadable from 'ui-component/Loadable'
import AuthGuard from 'utils/route-guard/AuthGuard'
import ContentCreatorGuard from 'utils/route-guard/ContentCreatorGuard'
import CustomerAdministratorGuard from 'utils/route-guard/CustomerAdministratorGuard'
import OpenGuard from 'utils/route-guard/OpenGuard'
import PlayerGuard from 'utils/route-guard/PlayerGuard'

import { Views } from 'features/enumerations'

const AddProfile = Loadable(
  lazy(() => import('../features/view/add/profile/view-add-profile')),
)
const ApprovalRequests = Loadable(
  lazy(
    () =>
      import(
        '../features/view/approval/requests/players/view-approval-requests-players'
      ),
  ),
)
const CoherentScreens = Loadable(
  lazy(() => import('../features/view/coherent/screens/view-coherent-screens')),
)
const ContentCreatorInvite = Loadable(
  lazy(
    () =>
      import(
        '../features/view/content/creator/invite/view-content-creator-invite'
      ),
  ),
)
const ContentCreators = Loadable(
  lazy(() => import('../features/view/content/creators/view-content-creators')),
)
const Houses = Loadable(
  lazy(
    () => import('../features/view/content/house/list/view-content-house-list'),
  ),
)
const ContentHouses = Loadable(
  lazy(() => import('../features/view/content/houses/view-content-houses')),
)
const CuratedMediaList = Loadable(
  lazy(
    () => import('../features/view/curated/media/list/view-curated-media-list'),
  ),
)
const CuratedSeriesList = Loadable(
  lazy(
    () =>
      import('../features/view/curated/series/list/view-curated-series-list'),
  ),
)
const CustomerSettings = Loadable(
  lazy(
    () => import('../features/view/customer/settings/view-customer-settings'),
  ),
)
const EditProfile = Loadable(
  lazy(() => import('../features/view/edit/profile/view-edit-profile')),
)
const Home = Loadable(lazy(() => import('../features/view/home/view-home')))
const MediaResources = Loadable(
  lazy(() => import('../features/view/media/resources/view-media-resources')),
)
const MetricConfiguration = Loadable(
  lazy(
    () =>
      import('../features/view/metric/configuration/view-metric-configuration'),
  ),
)
const CustomerAdministrator = Loadable(
  lazy(() => import('../features/view/metric/graphs/view-metric-graphs')),
)
const MobileConnect = Loadable(
  lazy(() => import('../features/view/mobile/connect/view-mobile-connect')),
)
const MobileLogin = Loadable(
  lazy(() => import('../features/view/mobile/login/view-mobile-login')),
)
const PGCPS = Loadable(lazy(() => import('../features/view/pgcps/view-pgcps')))
const Players = Loadable(
  lazy(() => import('../features/view/player/view-player')),
)
const PlayerRole = Loadable(
  lazy(() => import('../features/view/player/roles/view-player-roles')),
)
const PreviewCoherentScreen = Loadable(
  lazy(
    () =>
      import(
        '../features/view/preview/coherent/screen/view-preview-coherent-screen'
      ),
  ),
)
const ProcessingdDashboard = Loadable(
  lazy(
    () =>
      import('../features/view/processing/dashboard/view-processing-dashboard'),
  ),
)
const Search = Loadable(
  lazy(() => import('../features/view/search/screen/view-search-screen')),
)
const SelectProfile = Loadable(
  lazy(() => import('../features/view/select/profile/view-select-profile')),
)
const EditProfileSelect = Loadable(
  lazy(
    () =>
      import('../features/view/select/profile/edit/view-select-profile-edit'),
  ),
)
const Support = Loadable(
  lazy(() => import('../features/view/support/view-support')),
)
const SystemSettings = Loadable(
  lazy(() => import('../features/view/system/settings/view-system-settings')),
)
const Watch = Loadable(
  lazy(() => import('../features/view/watch/media/view-watch-media')),
)

const GeneratedRoutes = {
  path: '/',
  element: (
    <AuthGuard>
      <MainLayout />
    </AuthGuard>
  ),
  children: [
    {
      path: `${Views['Houses']}/`,
      element: (
        <ContentCreatorGuard>
          <Houses />
        </ContentCreatorGuard>
      ),
    },
    {
      path: `${Views['ContentHouses']}/:id`,
      element: (
        <ContentCreatorGuard>
          <ContentHouses />
        </ContentCreatorGuard>
      ),
    },
    {
      path: `${Views['ContentCreators']}/:id`,
      element: (
        <ContentCreatorGuard>
          <ContentCreators />
        </ContentCreatorGuard>
      ),
    },
    {
      path: `${Views['ContentCreators']}/`,
      element: (
        <ContentCreatorGuard>
          <ContentCreators />
        </ContentCreatorGuard>
      ),
    },
    {
      path: `${Views['ContentCreators']}/:id/:creator`,
      element: (
        <ContentCreatorGuard>
          <ContentCreators />
        </ContentCreatorGuard>
      ),
    },
    {
      path: `${Views['Players']}/:id`,
      element: (
        <PlayerGuard>
          <Players />
        </PlayerGuard>
      ),
    },
    {
      path: `${Views['Players']}/`,
      element: (
        <PlayerGuard>
          <Players />
        </PlayerGuard>
      ),
    },
    {
      path: `${Views['ApprovalRequests']}/`,
      element: (
        <PlayerGuard>
          <ApprovalRequests />
        </PlayerGuard>
      ),
    },
    {
      path: `${Views['CuratedSeriesList']}/`,
      element: (
        <PlayerGuard>
          <CuratedSeriesList />
        </PlayerGuard>
      ),
    },
    {
      path: `${Views['CuratedMediaList']}/`,
      element: (
        <PlayerGuard>
          <CuratedMediaList />
        </PlayerGuard>
      ),
    },
    {
      path: `${Views['CoherentScreens']}/`,
      element: (
        <PlayerGuard>
          <CoherentScreens />
        </PlayerGuard>
      ),
    },
    {
      path: `${Views['PreviewCoherentScreen']}/`,
      element: (
        <PlayerGuard>
          <PreviewCoherentScreen />
        </PlayerGuard>
      ),
    },
    {
      path: `${Views['PreviewCoherentScreen']}/:id`,
      element: (
        <PlayerGuard>
          <PreviewCoherentScreen />
        </PlayerGuard>
      ),
    },
    {
      path: `${Views['PreviewCoherentScreen']}/:id/:component`,
      element: (
        <PlayerGuard>
          <PreviewCoherentScreen />
        </PlayerGuard>
      ),
    },
    {
      path: `${Views['MediaResources']}/`,
      element: (
        <PlayerGuard>
          <MediaResources />
        </PlayerGuard>
      ),
    },
    {
      path: `${Views['MediaResources']}/:id`,
      element: (
        <PlayerGuard>
          <MediaResources />
        </PlayerGuard>
      ),
    },
    {
      path: `${Views['SystemSettings']}/`,
      element: (
        <PlayerGuard>
          <SystemSettings />
        </PlayerGuard>
      ),
    },
  ].flat(),
}
export function GetRoutes() {
  return [
    {
      path: '/',
      element: (
        <AuthGuard>
          <MinimalLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: `${Views['AddProfile']}/`,
          element: <AddProfile />,
        },
        {
          path: `${Views['EditProfile']}/:id`,
          element: <EditProfile />,
        },
        {
          path: `${Views['EditProfile']}/:id/profile-image`,
          element: <EditProfile />,
        },
        {
          path: `${Views['SelectProfile']}/`,
          element: <SelectProfile />,
        },
        {
          path: `${Views['SelectProfile']}/profile-image`,
          element: <SelectProfile />,
        },
        {
          path: `${Views['EditProfileSelect']}/`,
          element: <EditProfileSelect />,
        },
        {
          path: `${Views['Watch']}/`,
          element: <Watch />,
        },
        {
          path: `${Views['Watch']}/:id`,
          element: <Watch />,
        },
        {
          path: `${Views['Watch']}/:id/:media`,
          element: <Watch />,
        },
        {
          path: `${Views['Watch']}/:id/:media/:approvedMediaId`,
          element: <Watch />,
        },
        {
          path: `${Views['MobileLogin']}/`,
          element: <MobileLogin />,
        },
        {
          path: `${Views['MobileConnect']}/`,
          element: <MobileConnect />,
        },
        {
          path: `${Views['playwatchkidsmobile']}/`,
          element: <playwatchkidsmobile />,
        },
        {
          path: `${Views['playwatchkidsmobile']}/:id`,
          element: <playwatchkidsmobile />,
        },
        {
          path: `${Views['MetricConfiguration']}/`,
          element: (
            <PlayerGuard>
              <MetricConfiguration />
            </PlayerGuard>
          ),
        },
      ].flat(),
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <NetflixLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: `${Views['Search']}/`,
          element: <Search />,
        },
      ].flat(),
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: `${Views['ContentCreatorInvite']}/`,
          element: (
            <PlayerGuard>
              <ContentCreatorInvite />
            </PlayerGuard>
          ),
        },
        {
          path: `${Views['ProcessingdDashboard']}/`,
          element: (
            <PlayerGuard>
              <ProcessingdDashboard />
            </PlayerGuard>
          ),
        },
        {
          path: `${Views['CustomerSettings']}/`,
          element: (
            <PlayerGuard>
              <CustomerSettings />
            </PlayerGuard>
          ),
        },
        {
          path: `${Views['PlayerRole']}/:id`,
          element: (
            <PlayerGuard>
              <PlayerRole />
            </PlayerGuard>
          ),
        },
        {
          path: `${Views['PlayerRole']}/`,
          element: (
            <PlayerGuard>
              <PlayerRole />
            </PlayerGuard>
          ),
        },
      ].flat(),
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <YoutubeKidsLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: `${Views['Home']}/`,
          element: <Home />,
        },
        {
          path: `${Views['Home']}/:id`,
          element: <Home />,
        },
        {
          path: `${Views['Home']}/:id/:component`,
          element: <Home />,
        },
        {
          path: `${Views['Home']}/`,
          element: <Home />,
        },
        {
          path: `${Views['Home']}/:id`,
          element: <Home />,
        },
        {
          path: `${Views['Home']}/:id/:media`,
          element: <Home />,
        },
        {
          path: `${Views['Home']}/:id/:media/:approvedMediaId`,
          element: <Home />,
        },
      ].flat(),
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <SimpleHeaderLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: `${Views['CustomerAdministrator']}/`,
          element: (
            <CustomerAdministratorGuard>
              <CustomerAdministrator />
            </CustomerAdministratorGuard>
          ),
        },
      ].flat(),
    },
    {
      path: '/',
      element: (
        <OpenGuard>
          <MinimalLayout />
        </OpenGuard>
      ),
      children: [
        {
          path: `${Views['Support']}/`,
          element: <Support />,
        },
        {
          path: `${Views['PGCPS']}/`,
          element: <PGCPS />,
        },
      ].flat(),
    },
  ]
}
export default GeneratedRoutes
